/**
 * 贷款还款弹窗（还本期 / 提前还款）—— `DR-049` · 批次 84
 *
 * 依据：`Q域PRD` `AC-Q2.7` / `Q2.8` · `F域PRD` `AC-F1.19` / `F1.20` / `F1.21` · `DR-045` D3 · `DR-047` D5
 *
 * ## 🔴 三条端侧纪律
 *
 * 1. **零自算**：每期本金/利息的默认值来自服务端计划表；提前还款的结清金额取
 *    `scheduleRemainingCents`；**不给"缩期后每月还多少"的试算数字**（自己算 =
 *    把 `rescheduleSplits` 抄第二份，`AC-Q6.4` 禁止）。
 * 2. **不开 effect 初始化草稿**：计划表是异步来的 ⇒ 草稿用 `draft ?? 派生值` 在**渲染期**得出
 *    （㊸/`57` 批的同一取向：effect 里做初始化会让首屏停在空表单）。
 * 3. **结清要第二次点击**：期次全部作废是不可逆动作（同 `AC-Q2.5` 核销的范式）。
 *
 * ## 为什么 Body 单独导出
 *
 * `Dialog` 走 Portal，静态渲染（`renderToStaticMarkup`）读不到内容 ⇒
 * 断言必须打在**内容件**上（与 `RestoreDialogBody` / `ReimburseDialogBody` 同范式）。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { AccountView, PayLoanResultView, PrepayLoanResultView } from '@jiaying/shared';
import { getLoanSchedule, payLoanPeriod, prepayLoan } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { AccountPicker } from './AccountPicker.js';
import {
  PREPAY_MODE_OPTIONS,
  REPAY_CALIBER_HINT,
  SETTLE_AMOUNT_LOCKED_HINT,
  SETTLE_CONFIRM_HINT,
  amountLabel,
  buildPayRequest,
  buildPrepayRequest,
  defaultRepayDraft,
  payDoneText,
  pendingPeriodsOf,
  periodAmountTexts,
  periodPlanText,
  periodTitle,
  repayBlockedReason,
  settleAmountText,
  togglePeriod,
  withPeriodEdit,
  type PrepayMode,
  type RepayDraft,
} from '../features/loan-repay.js';

/** 一次最多渲染多少期待还期次（1200 期的房贷整列出来没法用） */
const VISIBLE_PERIODS = 12;

function apiMessages(e: unknown): readonly string[] {
  if (!(e instanceof ApiError)) return [String(e)];
  const details = e.body.details as { issues?: unknown } | undefined;
  const issues = Array.isArray(details?.issues)
    ? details.issues.filter((x): x is string => typeof x === 'string')
    : [];
  return issues.length > 0 ? issues : [e.message];
}

export interface LoanRepayDialogBodyProps {
  account: AccountView;
  /** 账本本地日历日 */
  today: string;
  /** 可选的付款账户（由页面从账户列表过滤好 —— 本组件不猜哪些能选） */
  counterOptions: readonly { id: string; label: string }[];
  onDone: (result: PayLoanResultView | PrepayLoanResultView) => void;
  onClose: () => void;
}

export function LoanRepayDialogBody({
  account,
  today,
  counterOptions,
  onDone,
  onClose,
}: LoanRepayDialogBodyProps): ReactElement {
  const scheduleQuery = useQuery({
    queryKey: ['loanSchedule', account.id],
    queryFn: () => getLoanSchedule(account.id),
  });
  const schedule = scheduleQuery.data;

  /**
   * 🔴 `null` = 还没被用户动过 ⇒ 用派生默认值（**渲染期派生，不开 effect**）。
   * 用户一动手就落到 `draft` 上，派生值不再参与。
   */
  const [draftState, setDraftState] = useState<RepayDraft | null>(null);
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [pending, setPending] = useState(false);
  /** 结清的第二次点击（同 `AC-Q2.5` 核销范式） */
  const [settleArmed, setSettleArmed] = useState(false);

  const defaultFrom = counterOptions[0]?.id ?? '';
  const draft: RepayDraft | null =
    draftState ?? (schedule === undefined ? null : defaultRepayDraft(schedule, today, defaultFrom));
  const setDraft = (next: RepayDraft): void => {
    setDraftState(next);
    setErrors([]);
    setSettleArmed(false);
  };

  const disabled = pending;

  // 🔴 三个条件都放在**同一个出口**里：这样下面的代码 TS 才知道 `schedule !== undefined`
  //    （分开写 `if (isPending) return` 之后 TS 仍然认为它可能是 undefined —— 批 84 实测）
  if (scheduleQuery.isPending || schedule === undefined || draft === null) {
    return (
      <>
        <DialogTitle>还款 · {account.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" data-testid="loan-repay-loading">
            正在读取还款计划…
          </Typography>
        </DialogContent>
      </>
    );
  }

  if (scheduleQuery.isError) {
    return (
      <>
        <DialogTitle>还款 · {account.name}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" data-testid="loan-repay-load-error">
            {apiMessages(scheduleQuery.error).join('；')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>关闭</Button>
        </DialogActions>
      </>
    );
  }

  const blocked = repayBlockedReason(schedule);
  const pendingPeriods = pendingPeriodsOf(schedule);
  const visiblePeriods = pendingPeriods.slice(0, VISIBLE_PERIODS);
  const hiddenCount = pendingPeriods.length - visiblePeriods.length;

  const submit = (): void => {
    if (draft.tab === 'pay') {
      const built = buildPayRequest(draft, schedule);
      if (!built.ok) {
        setErrors(built.errors);
        return;
      }
      setErrors([]);
      setPending(true);
      payLoanPeriod(account.id, built.request)
        .then((r) => onDone(r))
        .catch((e: unknown) => setErrors(apiMessages(e)))
        .finally(() => setPending(false));
      return;
    }

    // 提前结清：第一次点击只"上膛"
    if (draft.prepayMode === 'settle' && !settleArmed) {
      setSettleArmed(true);
      setErrors([]);
      return;
    }
    const built = buildPrepayRequest(draft, schedule);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors([]);
    setPending(true);
    prepayLoan(account.id, built.request)
      .then((r) => onDone(r))
      .catch((e: unknown) => setErrors(apiMessages(e)))
      .finally(() => setPending(false));
  };

  return (
    <>
      <DialogTitle>还款 · {account.name}</DialogTitle>
      <DialogContent dividers>
        {blocked !== null && (
          <Alert severity="info" data-testid="loan-repay-blocked">
            {blocked}
          </Alert>
        )}

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }} data-testid="loan-repay-errors">
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </Stack>
          </Alert>
        )}

        <ToggleButtonGroup
          exclusive
          size="small"
          value={draft.tab}
          onChange={(_, v: RepayDraft['tab'] | null) => {
            if (v !== null) setDraft({ ...draft, tab: v });
          }}
          sx={{ mt: 1.5 }}
          data-testid="loan-repay-tabs"
        >
          <ToggleButton value="pay">还本期</ToggleButton>
          <ToggleButton value="prepay">提前还款</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' }, mt: 1.5 }}>
          <TextField
            label="还款日"
            type="date"
            value={draft.dateText}
            onChange={(e) => setDraft({ ...draft, dateText: e.target.value })}
            // 🆕 批 `115`（T4）：原生日期控件（有日历），仍可键入
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'loan-repay-date' }}
            disabled={disabled}
          />
          {/*
            🆕 批 `115`（T4）：**换控件不换候选** —— 仍是页面筛好的 `counterOptions`，顺序原样。
            🔴 `allowEmpty` 保住旧的「请选择」空项（付款账户没填也能看计划、之后再选）。
            🔴 `testId` 保留 `loan-repay-from`（`LoanRepayDialog.spec` 靠它）。
          */}
          <AccountPicker
            label="付款账户"
            accounts={counterOptions.map((o) => ({ id: o.id, name: o.label }))}
            value={draft.fromAccountId}
            onChange={(id) => setDraft({ ...draft, fromAccountId: id })}
            disabled={disabled}
            allowEmpty
            testId="loan-repay-from"
            helperText="同币种账户"
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {draft.tab === 'pay' ? (
          <Box data-testid="loan-repay-pay-panel">
            <Typography variant="body2" color="text.secondary">
              {REPAY_CALIBER_HINT}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              已勾 {draft.selected.length} 期 · 还有 {pendingPeriods.length} 期待还
            </Typography>

            <Stack sx={{ mt: 1 }}>
              {visiblePeriods.map((p) => {
                const texts = periodAmountTexts(p, draft);
                const checked = draft.selected.includes(p.periodIndex);
                return (
                  <Box
                    key={p.periodIndex}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      mb: 1,
                      opacity: checked ? 1 : 0.65,
                    }}
                    data-testid={`loan-period-${String(p.periodIndex)}`}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => setDraft(togglePeriod(draft, p.periodIndex))}
                          disabled={disabled}
                          // 🔴 `data-testid` **直接挂组件**：MUI 的 `inputProps` 类型是
                          //    `InputHTMLAttributes`，字面量里带 `data-*` 会 `TS2353`（`57` 批踩过）
                          data-testid={`loan-period-check-${String(p.periodIndex)}`}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {periodTitle(p)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {periodPlanText(p, schedule.currency)}
                          </Typography>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr', mt: 0.5 }}>
                      <TextField
                        size="small"
                        label={amountLabel(schedule.currency)}
                        value={texts.principalText}
                        onChange={(e) =>
                          setDraft(withPeriodEdit(draft, p.periodIndex, { principalText: e.target.value }))
                        }
                        inputProps={{
                          'data-testid': `loan-period-principal-${String(p.periodIndex)}`,
                          inputMode: 'decimal',
                        }}
                        disabled={disabled}
                        helperText="本金"
                      />
                      <TextField
                        size="small"
                        label={amountLabel(schedule.currency)}
                        value={texts.interestText}
                        onChange={(e) =>
                          setDraft(withPeriodEdit(draft, p.periodIndex, { interestText: e.target.value }))
                        }
                        inputProps={{
                          'data-testid': `loan-period-interest-${String(p.periodIndex)}`,
                          inputMode: 'decimal',
                        }}
                        disabled={disabled}
                        helperText="利息（计入支出）"
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            {hiddenCount > 0 && (
              <Typography variant="caption" color="text.secondary" data-testid="loan-period-more">
                还有 {hiddenCount} 期未列出（先还完前 {VISIBLE_PERIODS} 期再打开即可看到后面的）
              </Typography>
            )}
          </Box>
        ) : (
          <Box data-testid="loan-repay-prepay-panel">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={draft.prepayMode}
              onChange={(_, v: PrepayMode | null) => {
                if (v !== null) setDraft({ ...draft, prepayMode: v });
              }}
              data-testid="loan-prepay-mode"
            >
              {PREPAY_MODE_OPTIONS.map((o) => (
                <ToggleButton key={o.value} value={o.value}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {PREPAY_MODE_OPTIONS.find((o) => o.value === draft.prepayMode)?.hint}
            </Typography>

            {draft.prepayMode === 'settle' ? (
              <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="loan-prepay-settle-hint">
                {SETTLE_CONFIRM_HINT}
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {SETTLE_AMOUNT_LOCKED_HINT}
                </Typography>
              </Alert>
            ) : null}

            <TextField
              fullWidth
              sx={{ mt: 1.5 }}
              label={amountLabel(schedule.currency)}
              value={
                draft.prepayMode === 'settle' ? settleAmountText(schedule) : draft.prepayAmountText
              }
              onChange={(e) => setDraft({ ...draft, prepayAmountText: e.target.value })}
              inputProps={{ 'data-testid': 'loan-prepay-amount', inputMode: 'decimal' }}
              disabled={disabled || draft.prepayMode === 'settle'}
              helperText={
                draft.prepayMode === 'settle'
                  ? '金额由系统按剩余本金算出（只读）'
                  : '必须小于剩余本金；要全部还清请选「提前结清」'
              }
            />
          </Box>
        )}

        <TextField
          fullWidth
          label="备注"
          value={draft.note}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          inputProps={{ 'data-testid': 'loan-repay-note', maxLength: 200 }}
          disabled={disabled}
          sx={{ mt: 1.5 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={disabled || blocked !== null}
          color={draft.tab === 'prepay' && draft.prepayMode === 'settle' && settleArmed ? 'error' : 'primary'}
          data-testid="loan-repay-submit"
        >
          {draft.tab === 'prepay' && draft.prepayMode === 'settle' && settleArmed
            ? '确认结清'
            : draft.tab === 'pay'
              ? '确认还款'
              : '确认提前还款'}
        </Button>
      </DialogActions>
    </>
  );
}

export interface LoanRepayDialogProps extends LoanRepayDialogBodyProps {
  open: boolean;
}

export function LoanRepayDialog({ open, onClose, ...rest }: LoanRepayDialogProps): ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <LoanRepayDialogBody {...rest} onClose={onClose} />
    </Dialog>
  );
}

/** 还款成功后的一句话（页面用来做提示 —— 金额全部来自服务端结果） */
export function payResultText(
  result: PayLoanResultView | PrepayLoanResultView,
  currency: string,
): string {
  if ('principalCents' in result) {
    return payDoneText(result.principalCents, result.interestCents, currency);
  }
  if (result.mode === 'settle') {
    return `已结清（作废 ${String(result.voidedPeriods)} 期）`;
  }
  return `已提前还款：重排 ${String(result.rescheduledPeriods)} 期 · 剩余 ${String(result.remainingPeriods)} 期`;
}
